import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ReactMarkdown from 'react-markdown';
import { resources } from '../../../core/services';

const BLANK_TEMPLATE = { value: 'blank', label: 'Blank', content: '' };

interface EmptyProjectGeneratorProps {
  /** The project story being written — owned by the page so it survives a failed generation attempt. */
  storyPrompt: string;
  onStoryPromptChange: (value: string) => void;
  error: string | null;
  onClearError: () => void;
  onGenerate: () => void;
}

/**
 * First-time "no documents yet" screen: a Markdown editor for the project
 * story (with a template picker and a preview mode) and the button that
 * generates the first product spec from it.
 */
export function EmptyProjectGenerator({
  storyPrompt,
  onStoryPromptChange,
  error,
  onClearError,
  onGenerate,
}: EmptyProjectGeneratorProps) {
  const [editorMode, setEditorMode] = useState<'edit' | 'view'>('edit');
  const [selectedTemplate, setSelectedTemplate] = useState('blank');

  const templatesQuery = useQuery({
    queryKey: ['projectGeneratePromptTemplates'],
    queryFn: () => resources.projectGeneratePromptTemplates.list({ pageSize: 100, sort: 'order' }),
  });

  const promptTemplates = [
    BLANK_TEMPLATE,
    ...(templatesQuery.data?.data ?? []).map((t) => ({
      value: t.label,
      label: t.name,
      content: t.content,
    })),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 3 }}>
      {/* Error alert */}
      {error && (
        <Alert severity="error" onClose={onClearError}>
          {error}
        </Alert>
      )}

      {/* Intro */}
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
        This project has no documents yet. Describe your project idea below and generate a
        Product Requirements Document to get started.
      </Typography>

      {/* Template selector + Edit/Preview toggle */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Template</InputLabel>
          <Select
            value={selectedTemplate}
            label="Template"
            onChange={(e) => {
              const tpl = promptTemplates.find((t) => t.value === e.target.value);
              setSelectedTemplate(e.target.value);
              if (tpl) onStoryPromptChange(tpl.content);
            }}
          >
            {promptTemplates.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={editorMode}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) setEditorMode(val); }}
        >
          <ToggleButton value="edit" sx={{ gap: 0.5, px: 1.5 }}>
            <EditNoteIcon fontSize="small" />
            <Typography variant="caption" fontWeight={600}>Edit</Typography>
          </ToggleButton>
          <ToggleButton value="view" sx={{ gap: 0.5, px: 1.5 }}>
            <VisibilityOutlinedIcon fontSize="small" />
            <Typography variant="caption" fontWeight={600}>Preview</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Markdown editor */}
      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {editorMode === 'edit' ? (
          <TextField
            multiline
            fullWidth
            placeholder="Describe your project story here using Markdown…&#10;&#10;Example:&#10;## Overview&#10;We are building a platform that helps teams…"
            value={storyPrompt}
            onChange={(e) => onStoryPromptChange(e.target.value)}
            sx={{
              flex: 1,
              minHeight: 0,
              '& .MuiInputBase-root': {
                bgcolor: 'background.paper',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                alignItems: 'flex-start',
                height: '100%',
                overflow: 'auto',
              },
              '& .MuiInputBase-input': {
                height: '100% !important',
                overflowY: 'auto !important',
                boxSizing: 'border-box',
              },
            }}
          />
        ) : (
          <Paper
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              p: 3,
              '& h1,& h2,& h3,& h4': { mt: 2, mb: 1, fontWeight: 700 },
              '& p': { mb: 1.5 },
              '& ul,& ol': { pl: 2.5, mb: 1.5 },
              '& li': { mb: 0.5 },
              '& code': { fontFamily: 'monospace', fontSize: '0.85em', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 },
              '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflowX: 'auto' },
              '& blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 2, color: 'text.secondary', my: 1.5 },
            }}
          >
            {storyPrompt ? (
              <ReactMarkdown>{storyPrompt}</ReactMarkdown>
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                Nothing to preview yet. Switch to Edit and write your project story.
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Generate button */}
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          size="large"
          startIcon={<AutoAwesomeIcon />}
          disabled={!storyPrompt.trim()}
          onClick={onGenerate}
          sx={{ fontWeight: 700, letterSpacing: '0.04em' }}
        >
          GENERATE PRODUCT SPEC
        </Button>
      </Stack>
    </Box>
  );
}
